import { checkboxField } from './form/checkbox'
import { descriptionField } from './form/description'
import { fileField } from './form/file'
import { radioField } from './form/radio'
import { selectField } from './form/select'
import { textField } from './form/text'
import { h } from './utils'

let _departments: Promise<any> | undefined

export async function renderOpenVacancyForm(container: HTMLElement) {
  const fields: HTMLElement[] = []
  const customFields: { fieldName: string; required: boolean }[] = ZC_VACANCY_OPEN_FORM_FIELDS
    ? JSON.parse(ZC_VACANCY_OPEN_FORM_FIELDS).map((fieldName: string) =>
        fieldName.endsWith('*')
          ? { fieldName: fieldName.slice(0, -1), required: true }
          : { fieldName, required: false },
      )
    : []
  const hiddenFields: string[] = ZC_VACANCY_OPEN_FORM_FIELDS_HIDDEN
    ? JSON.parse(ZC_VACANCY_OPEN_FORM_FIELDS_HIDDEN)
    : []
  const requiredFields = customFields.filter(({ required }) => required)
  const filteredFields: HTMLElement[] = []
  const data = await fetch(
    `https://${ZC_HANDLE}.cockpit.recruitnow.${ZC_DOMAIN}/api/opencandidateenrollment/CandidateEnrollmentForms-${ZC_FORM_SUFFIX}`,
  ).then((res) => res.json())
  const errors: Record<string, HTMLSpanElement> = {}

  for (const step of data.steps) {
    for (const block of step.blocks) {
      for (const field of block.fields) {
        if (requiredFields.some(({ fieldName }) => fieldName === field.name)) {
          field.required = true
        }

        if (field.type === 'Checkbox') {
          if (field.description) {
            const { field: fieldEl } = descriptionField({
              html: field.description,
              name: field.name,
              hidden: hiddenFields.includes(field.name),
            })
            fields.push(fieldEl)
          }
          const { field: fieldEl, error } = checkboxField({
            ...field,
            label: field.options[0]?.label,
            hidden: hiddenFields.includes(field.name),
          })
          fields.push(fieldEl)
          errors[field.name] = error
        } else if (field.type === 'LegacyFile') {
          const { field: fieldEl, error } = fileField({ ...field, hidden: hiddenFields.includes(field.name) })
          fields.push(fieldEl)
          errors[field.name] = error
        } else if (field.type === 'Department') {
          const { field: fieldEl, error } = selectField({
            ...field,
            choices: await getDepartments(),
            hidden: hiddenFields.includes(field.name),
            defaultValue: ZC_VACANCY_OPEN_FORM_DEFAULT_DEPARTMENT ?? field.defaultValue,
          })
          fields.push(fieldEl)
          errors[field.name] = error
        } else if (field.type === 'Radio') {
          const { field: fieldEl, error } = radioField({
            ...field,
            choices: field.options.map((option: any, i: number) => ({
              value: option.key,
              label: option.label,
              selected: i === 0,
            })),
          })
          fields.push(fieldEl)
          errors[field.name] = error
        } else {
          const { field: fieldEl, error } = textField({ ...field, hidden: hiddenFields.includes(field.name) })
          fields.push(fieldEl)
          errors[field.name] = error
        }
      }
    }
  }

  if (customFields.length) {
    for (const { fieldName } of customFields) {
      const index = fields.findIndex((field) => field.getAttribute('data-name') === fieldName)

      if (index > -1) {
        filteredFields.push(fields[index])
        fields.splice(index, 1)
      }
    }
  } else {
    filteredFields.push(...fields)
  }

  const form = h(
    'form',
    { class: 'vbzc-vacancy-form' },
    {
      children: [
        ...filteredFields,
        h('button', { class: 'vbzc-button vbzc-button-filled', type: 'submit' }, { text: data.config.submitText }),
      ],
      parent: container,
    },
  ) as HTMLFormElement

  form.addEventListener('submit', async (event) => {
    event.preventDefault()

    const _formData = new FormData(form)
    const formData = new FormData()
    const responses: { type: string; name: string; response: any }[] = []

    for (const step of data.steps) {
      for (const block of step.blocks) {
        for (const field of block.fields) {
          if (filteredFields.some((filteredField) => filteredField.getAttribute('data-name') === field.name)) {
            let value = _formData.get(field.name)

            if (field.name === 'MobilePhonenumber' && typeof value === 'string' && value.trim()) {
              value = String(value ?? '').replace(/[^0-9+]/g, '')

              if (!value.startsWith('00') && !value.startsWith('+')) {
                value = ZC_DEFAULT_COUNTRY_CODE + value
              } else if (value.startsWith('0') && !value.startsWith('00')) {
                value = ZC_DEFAULT_COUNTRY_CODE + value.slice(1)
              }
            }

            if (field.type === 'LegacyFile') {
              const file = value as File
              formData.append(field.name, file)
              responses.push({ type: field.type, name: field.name, response: file.name })
            } else if (field.type === 'Checkbox') {
              responses.push({ type: field.type, name: field.name, response: value === 'on' })
            } else {
              responses.push({ type: field.type, name: field.name, response: value })
            }

            if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
              errors[field.name].textContent = ZC_REQUIRED_MESSAGE
            } else if (field.validation && typeof value === 'string' && !new RegExp(field.validation).test(value)) {
              errors[field.name].textContent = ZC_VALIDATION_MESSAGE
            } else {
              errors[field.name].textContent = ''
            }
          }
        }
      }
    }

    formData.append(
      'createOpenCandidateEnrollmentResource',
      JSON.stringify({
        attachments: [],
        opencandidateenrollment: {
          formId: `CandidateEnrollmentForms-${ZC_FORM_SUFFIX}`,
          responses,
          campaignSource: {},
        },
      }),
    )

    const firstError = Object.values(errors).find((error) => error.textContent)
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    await fetch(`https://${ZC_HANDLE}.cockpit.recruitnow.${ZC_DOMAIN}/api/opencandidateenrollment`, {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        form.innerHTML = `<p>${ZC_SUCCESS_MESSAGE}</p>`
      })
      .catch((error) => {
        console.log(error)
      })
  })
}

export async function getDepartments() {
  if (!_departments) {
    _departments = fetch(`https://${ZC_HANDLE}.cockpit.recruitnow.${ZC_DOMAIN}/api/public/departments`)
  }

  return _departments.then((res) =>
    res
      .json()
      .then((departments: any) =>
        departments.map((department: any) => ({ value: department.id, label: department.name })),
      ),
  )
}
